import Colors from '@figma/panda/Colors'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import FeatherIcon from 'feather-icons-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { SelectController, TextFieldController } from '../../../components/react-hook-components'
import { type CreateTemplateDialogProps, type CreateTemplateFormData, SupportType } from './types'

export const CreateTemplateDialog = ({ isOpen, onClose, onSave, onError }: CreateTemplateDialogProps) => {
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<CreateTemplateFormData>({
    defaultValues: {
      name: '',
      externalName: '',
      eligibility: {
        partners: {
          type: SupportType.ALL,
          partners: [],
        },
        currencies: {
          type: SupportType.ALL,
          currencies: [],
        },
        countries: {
          type: SupportType.ALL,
          countries: [],
        },
      },
    },
    mode: 'onChange',
  })

  const getSupportTypeOptions = () => {
    return [
      { value: SupportType.ALL, label: 'All' },
      { value: SupportType.NONE, label: 'None' },
      { value: SupportType.SPECIFIC, label: 'Specific' },
    ]
  }

  const handleSubmit = async (data: CreateTemplateFormData) => {
    setIsLoading(true)
    try {
      await onSave(data)
    } catch (error) {
      if (onError && error instanceof Error) {
        onError(error)
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <Box sx={{ padding: '32px', paddingBottom: '24px' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6">Create new template</Typography>
          <IconButton size="small" onClick={onClose} data-testid="modal-close-button">
            <FeatherIcon icon="x" size={16} />
          </IconButton>
        </Stack>
      </Box>
      <Box sx={{ padding: '32px', paddingTop: '16px' }}>
        <Stack width="100%" spacing="32px">
          <TextFieldController control={form.control} name="name" labelText="Template name" smallCaseInputProps={{ autoComplete: 'off' }} />
          <TextFieldController
            control={form.control}
            name="externalName"
            labelText="External name"
            smallCaseInputProps={{ autoComplete: 'off' }}
          />
          <Typography variant="subtitle2" sx={{ mt: '8px' }}>
            Eligibility
          </Typography>
          <SelectController
            control={form.control}
            name="eligibility.partners.type"
            labelText="Partners"
            options={getSupportTypeOptions()}
          />
          <SelectController
            control={form.control}
            name="eligibility.currencies.type"
            labelText="Currencies"
            options={getSupportTypeOptions()}
          />
          <SelectController
            control={form.control}
            name="eligibility.countries.type"
            labelText="Countries"
            options={getSupportTypeOptions()}
          />
          <Box sx={{ pt: '4px' }}>
            <Button
              variant="contained"
              onClick={form.handleSubmit(handleSubmit)}
              disabled={!form.formState.isValid || isLoading}
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isLoading && <CircularProgress size={16} sx={{ mr: 1, color: Colors.baseWhite }} />}
              Create
            </Button>
          </Box>
        </Stack>
      </Box>
    </Dialog>
  )
}
